import type { DosageUnit } from './constants';
import { DOSAGE_UNITS } from './constants';

interface DosageMatch {
  amount: number;
  unit: DosageUnit; // as listed in DOSAGE_UNITS, whatever the case; UNIT_BY_FORMULATION gives the unit to convert to
  position: [number, number]; // start, end position of the matched text, trailing punctuation included
}

// \b - word boundary, so a longer word ending in "max" ("Submax daily dose 3g") does not match
// max(?:imum)?\s+daily\s+(?:dose|dosage) - "maximum daily dose", mandatory
// \s*:?\s* - optional colon, e.g. "Maximum daily dose: 3.0mL"
// (\d*\.?\d+) - amount, mandatory. 3, 3.0 and .25 all match; 1.1.1 does not
// \s* - optional space before the unit, e.g. "3.0 g" or "8 Capsules"
// (units)\b - unit from DOSAGE_UNITS, mandatory
// [;,.]? - optional trailing punctuation, part of the matched position
const MAX_DAILY_DOSE_REGEX = new RegExp(
  `\\bmax(?:imum)?\\s+daily\\s+(?:dose|dosage)\\s*:?\\s*(\\d*\\.?\\d+)\\s*(${DOSAGE_UNITS.join('|')})\\b[;,.]?`,
  'i',
);

/** The DOSAGE_UNITS spelling of a unit matched in any case, e.g. 'mL' for 'ML' */
const toDosageUnit = (matchedUnit: string) =>
  DOSAGE_UNITS.find((unit) => unit.toLowerCase() === matchedUnit.toLowerCase());

/**
 * Reads the maximum daily dose in dosage text, e.g. 'Maximum daily dose 2.6g' or 'Maximum daily dose: 8 Capsules'. The
 * text is left as it is; `position` tells the caller where the phrase sits.
 */
export const parseMaxDailyDose = (text: string): DosageMatch | undefined => {
  const result = MAX_DAILY_DOSE_REGEX.exec(text);
  const unit = result && toDosageUnit(result[2]);
  if (!result || !unit) {
    return undefined;
  }

  return {
    amount: Number(result[1]),
    unit,
    position: [result.index, result.index + result[0].length],
  };
};
